import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, CheckCircle2, XCircle, Loader2, RefreshCw, X, Send } from "lucide-react";
import api, { formatDetail } from "@/lib/api";

export function StuckBanner({ count, minutes, active, onToggle, testid }) {
    if (!count && !active) return null;
    return (
        <div data-testid={`${testid}-stuck-banner`}
             className="flex flex-wrap items-center gap-3 bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-3 py-2 text-xs">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
            <span className="font-semibold">{count} order{count === 1 ? "" : "s"} stuck &gt; {minutes >= 60 && minutes % 60 === 0 ? `${minutes / 60}h` : `${minutes}m`}</span>
            <span className="text-amber-800/80 hidden sm:inline">still in progress — follow up with the customer or check status.</span>
            <button onClick={onToggle} data-testid={`${testid}-stuck-review`}
                    className="ml-auto px-2.5 py-1 rounded-md bg-amber-600 text-white font-semibold hover:bg-amber-700 transition-colors">
                {active ? "Show all" : "Review"}
            </button>
        </div>
    );
}

export const StuckChip = ({ testid }) => (
    <span data-testid={testid} className="px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-amber-500 text-white">Stuck</span>
);

export function CallbackIcon({ cb, testid }) {
    if (!cb) return null;
    const ok = cb.delivered;
    const Icon = ok ? CheckCircle2 : XCircle;
    return (
        <span data-testid={testid} title={`Callback ${ok ? "delivered" : "failed"}${cb.status_code ? ` · HTTP ${cb.status_code}` : ""} · ${new Date(cb.created_at).toLocaleString()}`}>
            <Icon className={`w-3.5 h-3.5 ${ok ? "text-emerald-600" : "text-rose-500"}`} />
        </span>
    );
}

export function BulkBar({ ids, bulkPath, onDone, onClear, testid }) {
    const [busy, setBusy] = useState(false);
    const [res, setRes] = useState(null);
    const [err, setErr] = useState("");
    const run = async () => {
        setBusy(true); setErr("");
        try { const r = await api.post(bulkPath, { order_ids: ids }); setRes(r.data); onDone(); }
        catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };
    useEffect(() => { if (!ids.length) setErr(""); }, [ids.length]);
    if (!ids.length && !res) return null;
    return createPortal(
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[90] w-[min(40rem,calc(100vw-2rem))]" data-testid={`${testid}-bulkbar`}>
            <div className="bg-slate-900 text-white rounded-xl shadow-2xl px-4 py-3 text-xs space-y-2">
                {res && (
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1" data-testid={`${testid}-bulk-result`}>
                        <span className="font-semibold">{res.checked} checked</span>
                        <span className="text-emerald-300">{res.success} captured</span>
                        <span className="text-rose-300">{res.failed} failed</span>
                        {res.cancelled > 0 && <span className="text-slate-300">{res.cancelled} cancelled</span>}
                        <span className="text-amber-300">{res.in_progress} still in progress</span>
                        {res.errors > 0 && <span className="text-rose-300">{res.errors} errors</span>}
                        <button onClick={() => setRes(null)} data-testid={`${testid}-bulk-result-close`} className="ml-auto p-0.5 text-slate-400 hover:text-white"><X className="w-3.5 h-3.5" /></button>
                    </div>
                )}
                {ids.length > 0 && (
                    <div className="flex items-center gap-3">
                        <span className="font-semibold" data-testid={`${testid}-bulk-count`}>{ids.length} selected</span>
                        {err && <span className="text-rose-300 truncate">{err}</span>}
                        <button onClick={run} disabled={busy} data-testid={`${testid}-bulk-check`}
                                className="ml-auto px-3 py-1.5 rounded-md bg-white text-slate-900 font-semibold flex items-center gap-1.5 hover:bg-slate-100 disabled:opacity-60">
                            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Check status
                        </button>
                        <button onClick={onClear} disabled={busy} data-testid={`${testid}-bulk-clear`} className="px-2 py-1.5 text-slate-300 hover:text-white">Clear</button>
                    </div>
                )}
            </div>
        </div>, document.body);
}

const SOURCE_LABEL = { webhook: "Vendor webhook", verify_auto: "Auto-verify", verify_manual: "Manual check", verify_bulk: "Bulk check", manual_resend: "Resend" };

export function CallbackLog({ orderId, paths }) {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [openId, setOpenId] = useState(null);
    const load = () => api.get(paths.callbacks(orderId)).then((r) => setData(r.data)).catch((e) => setErr(formatDetail(e?.response?.data?.detail) || e.message));
    useEffect(() => { load(); }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps
    const resend = async () => {
        setBusy(true); setErr("");
        try { await api.post(paths.resend(orderId)); await load(); }
        catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };
    const items = data?.items || [];
    return (
        <section className="border border-slate-200 rounded-md" data-testid="payin-drawer-callbacks">
            <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
                <div>
                    <div className="text-xs font-medium text-slate-700">Callbacks</div>
                    <div className="text-[10px] text-slate-400 font-mono truncate max-w-[260px]" title={data?.callback_url || ""}>{data ? (data.callback_url || "No callback URL configured") : "…"}</div>
                </div>
                <button onClick={resend} disabled={busy || !data?.callback_url} data-testid="payin-drawer-callback-resend"
                        className="px-2.5 py-1 rounded-md border border-slate-200 text-[11px] font-semibold flex items-center gap-1.5 hover:bg-slate-50 disabled:opacity-50">
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Resend
                </button>
            </div>
            {err && <div className="px-3 py-2 text-[11px] text-rose-700 bg-rose-50" data-testid="payin-drawer-callback-error">{err}</div>}
            {data && items.length === 0 && <div className="px-3 py-3 text-[11px] text-slate-400 italic" data-testid="payin-drawer-callbacks-empty">No callbacks sent for this order yet.</div>}
            <ul className="divide-y divide-slate-100">
                {items.map((d, i) => (
                    <li key={`${d.delivery_id}-${i}`} data-testid={`payin-callback-${i}`}>
                        <button type="button" onClick={() => setOpenId(openId === i ? null : i)} className="w-full px-3 py-2 flex items-center gap-2 text-left hover:bg-slate-50">
                            <CallbackIcon cb={d} />
                            <span className="text-[11px] font-mono text-slate-800">{d.event}</span>
                            <span className={`text-[10px] font-mono ${d.delivered ? "text-emerald-700" : "text-rose-600"}`}>{d.status_code ? `HTTP ${d.status_code}` : "no response"}</span>
                            <span className="text-[10px] text-slate-400 ml-auto whitespace-nowrap">{SOURCE_LABEL[d.source] || d.source || "Vendor webhook"} · {new Date(d.created_at).toLocaleString()}</span>
                        </button>
                        {openId === i && (
                            <div className="px-3 pb-2 space-y-1">
                                <div className="text-[10px] text-slate-500 font-mono break-all">{d.url}</div>
                                <pre className="text-[10px] font-mono bg-slate-50 border border-slate-100 rounded p-2 max-h-32 overflow-auto whitespace-pre-wrap break-all">{d.error || d.response_body || "(empty response)"}</pre>
                            </div>
                        )}
                    </li>
                ))}
            </ul>
        </section>
    );
}
